import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import StopIcon from '@mui/icons-material/Stop';
import {Box, Link} from '@mui/material';
import Lottie from 'lottie-react';
import {useTranslation} from 'react-i18next';

import {SendInputIcon} from '@/app/shared-components/SendInputIcon';
import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {SnackbarType} from '@/app/shared-components/Snackbar/SnackbarTypes';
import {LINKS} from '@/constants/LinksConstants';
import ModakLoaderAnimation from '@/lotties/ModakLoader.json';
import {COLORS} from '@/styles/colors';
import {convertHeicToJpeg} from '@/utils/convertHeicToJpeg';
import logError from '@/utils/errors/logError';
import {isHeicFile} from '@/utils/isHeicFile';

import {FilePreview} from './FilePreview';
import {FileUploader} from './FileUploader';

import '../styles';

const FILES_ACCEPTED = {
  IMAGE: 'image/',
  PDF: 'application/pdf',
} as const;

const TIMER_INTERVAL_MS = 1000;
const EMPTY_FILE_SIZE = 0;
const RECORDING_ERRORS = {
  NOT_ALLOWED: 'not-allowed',
} as const;

interface Props {
  isLoading: boolean;
  sendMessage: (text: string) => void;
  handleSaveFileImage: (file: File) => void;
  fileImagesPreview: string | null;
  setFileImagesPreview: (val: string | null) => void;
  stopMessage: () => void;
  shouldShowDisclaimer: boolean;
}

export const MessageInput = ({
  isLoading,
  sendMessage,
  handleSaveFileImage,
  fileImagesPreview,
  setFileImagesPreview,
  stopMessage,
  shouldShowDisclaimer,
}: Props) => {
  const {t} = useTranslation();

  const [text, setText] = useState('');
  const [filename, setFilename] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [interimText, setInterimText] = useState('');
  const [recordingTime, setRecordingTime] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const isDisabled = useMemo(
    () => !text.trim() || isLoading || isRecording,
    [text, isLoading, isRecording],
  );

  //#region Message actions

  const handleStop = useCallback(() => {
    stopMessage();
  }, [stopMessage]);

  const handleSend = useCallback(() => {
    if (text.trim()) {
      sendMessage(text.trim());
      setText('');
    }
  }, [sendMessage, text]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && text.trim()) {
        e.preventDefault();
        if (isDisabled) return;
        handleSend();
      }
    },
    [handleSend, isDisabled, text],
  );

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setText(e.target.value);
    },
    [],
  );

  //#endregion

  //#region File handling

  const isValidFileType = useCallback((type: string) => {
    return type.startsWith(FILES_ACCEPTED.IMAGE) || type === FILES_ACCEPTED.PDF;
  }, []);

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (!file) return;

      if (file.size === EMPTY_FILE_SIZE) {
        showSnackbar({
          message: t('split_bills_page.empty_file_error'),
          variant: SnackbarType.WARNING,
        });
        return;
      }

      if (!isValidFileType(file.type)) {
        showSnackbar({
          message: t('split_bills_page.invalid_file_type_error'),
          variant: SnackbarType.WARNING,
        });
        return;
      }

      const isHeic = isHeicFile(file);
      let fileToUse: File | Blob = file;

      if (isHeic) {
        try {
          setIsConverting(true);
          fileToUse = await convertHeicToJpeg(file);
        } catch (err) {
          logError(err, {
            fileName: 'MessageInput.tsx',
            functionName: 'handleFileSelect',
            customTitle: 'HEIC conversion failed',
            data: {
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
            },
            severity: 'warning',
          });
          showSnackbar({
            message: t('split_bills_page.convert_heic_error'),
            variant: SnackbarType.ERROR,
          });
          setIsConverting(false);
          return;
        } finally {
          setIsConverting(false);
        }
      }

      if (file.type.startsWith(FILES_ACCEPTED.IMAGE)) {
        const reader = new FileReader();

        reader.onerror = () => {
          logError(new Error('Error reading file'), {
            fileName: 'MessageInput.tsx',
            functionName: 'handleFileSelect',
            customTitle: 'File reading failed',
            data: {
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
            },
            severity: 'warning',
          });
        };

        reader.onloadend = () => {
          setFileImagesPreview(reader.result as string);
          handleSaveFileImage(
            isHeic
              ? new File([fileToUse], `${file.name}.jpg`, {
                  type: 'image/jpeg',
                })
              : file,
          );
        };

        reader.readAsDataURL(fileToUse);
      } else {
        setFileImagesPreview(file.name);
        handleSaveFileImage(file);
      }
      setFilename(file.name);
    },
    [handleSaveFileImage, setFileImagesPreview, t, isValidFileType],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = event.clipboardData.items;
      for (const item of items) {
        if (isValidFileType(item.type)) {
          const file = item.getAsFile();
          if (file) {
            handleFileSelect(file);
          }
        }
      }
    },
    [handleFileSelect, isValidFileType],
  );

  //#endregion

  //#region Drag and Drop

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      files.forEach(handleFileSelect);
    },
    [handleFileSelect],
  );

  //#endregion

  //#region Recording timer

  useEffect(() => {
    if (isRecording) {
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, TIMER_INTERVAL_MS);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setRecordingTime(0);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording]);

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  //#endregion

  //#region Speech Recognition

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return;

    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;

    recognitionRef.current.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        setText(prev => prev + finalTranscript);
        setInterimText('');
      } else {
        setInterimText(interimTranscript);
      }
    };

    recognitionRef.current.onerror = (event: SpeechRecognitionErrorEvent) => {
      logError(new Error(`Speech recognition error: ${event.error}`), {
        fileName: 'MessageInput.tsx',
        functionName: 'SpeechRecognition.onerror',
        customTitle: 'Speech recognition failed',
        data: {
          error: event.error,
          message: event.message,
        },
        severity: 'warning',
      });
      setIsRecording(false);
      if (event.error === RECORDING_ERRORS.NOT_ALLOWED) {
        showSnackbar({
          message: t('split_bills_page.microphone_permission_denied'),
          variant: SnackbarType.ERROR,
        });
      }
    };

    recognitionRef.current.onend = () => {
      setIsRecording(false);
      setInterimText('');
    };

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //#endregion

  //#region Toggle Recording

  const toggleRecording = useCallback(() => {
    if (!recognitionRef.current) {
      showSnackbar({
        message: t('split_bills_page.speech_recognition_not_supported'),
        variant: SnackbarType.WARNING,
      });
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
      setInterimText('');
    } else {
      try {
        recognitionRef.current.start();
        setIsRecording(true);
      } catch (error) {
        logError(error, {
          fileName: 'MessageInput.tsx',
          functionName: 'toggleRecording',
          customTitle: 'Failed to start speech recognition',
          severity: 'warning',
        });
        showSnackbar({
          message: t('split_bills_page.speech_recognition_start_error'),
          variant: SnackbarType.ERROR,
        });
      }
    }
  }, [isRecording, t]);

  //#endregion

  //#region Render

  const placeHolderMemoized = useMemo(() => {
    return fileImagesPreview
      ? t('split_bills_page.input_placeholder_with_image')
      : t('split_bills_page.input_placeholder');
  }, [fileImagesPreview, t]);

  return (
    <>
      {!shouldShowDisclaimer && (
        <Box>
          <div className="disclaimer-text center">
            <div>{t('split_bills_page.disclaimer')}</div>
            <div>
              {t('split_bills_page.disclaimer_2')}
              <Link
                href={LINKS.PRIVACY_POLICY}
                target="_blank"
                rel="noopener noreferrer"
                className="text-link">
                {t('split_bills_page.disclaimer_link')}
              </Link>
            </div>
          </div>
        </Box>
      )}
      <div className="input-with-preview-row">
        {isConverting && (
          <Lottie
            animationData={ModakLoaderAnimation}
            loop
            style={{
              width: 64,
              height: 64,
            }}
          />
        )}
        <FilePreview
          filename={filename}
          preview={fileImagesPreview}
          onRemove={() => setFileImagesPreview(null)}
        />
        <div
          className={`chatbot-input-box ${isDragging ? 'drag-over' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}>
          <textarea
            id="chatbot-textarea"
            className="chatbot-textarea"
            placeholder={placeHolderMemoized}
            value={text + interimText}
            onChange={handleOnChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            title={t('split_bills_page.input_tooltip')}
          />

          <div className="chatbot-input-actions">
            <div className="left-actions">
              <FileUploader onFileSelected={handleFileSelect} />
            </div>

            <div className="right-actions">
              {isRecording && (
                <div className="recording-indicator">
                  <div className="radio-wave">
                    <span className="wave-bar"></span>
                    <span className="wave-bar"></span>
                    <span className="wave-bar"></span>
                  </div>
                  <span className="recording-time">
                    {formatTime(recordingTime)}
                  </span>
                </div>
              )}
              {!isLoading && (
                <button
                  id="chatbot-microphone-button"
                  className={`chatbot-send-button ${isRecording ? 'recording' : ''}`}
                  onClick={toggleRecording}
                  type="button"
                  title={
                    isRecording
                      ? t('split_bills_page.stop_recording_tooltip')
                      : t('split_bills_page.start_recording_tooltip')
                  }>
                  {isRecording ? (
                    <MicOffIcon
                      aria-label={t('split_bills_page.stop_recording_tooltip')}
                      style={{fill: COLORS.RED_CINNABAR}}
                    />
                  ) : (
                    <MicIcon
                      aria-label={t('split_bills_page.start_recording_tooltip')}
                      style={{fill: COLORS.CONTENT_LIGHT_TERTIARY}}
                    />
                  )}
                </button>
              )}
              {isLoading ? (
                <button
                  id="chatbot-stop-button"
                  type="button"
                  className="chatbot-send-button loading"
                  title={t('split_bills_page.stop_generating_response_tooltip')}
                  aria-label={t(
                    'split_bills_page.stop_generating_response_tooltip',
                  )}
                  onClick={handleStop}>
                  <StopIcon style={{fill: COLORS.CONTENT_LIGHT_TERTIARY}} />
                </button>
              ) : (
                <button
                  id="chatbot-send-button"
                  className={`chatbot-send-button ${text.trim() ? 'is-typing' : ''}`}
                  disabled={isDisabled}
                  title={t('split_bills_page.send_message_tooltip')}
                  aria-label={t('split_bills_page.send_message_tooltip')}
                  onClick={handleSend}>
                  <SendInputIcon
                    opacity={isDisabled ? 0.38 : 1}
                    fill={isDisabled ? COLORS.BLACK : COLORS.WHITE}
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
  //#endregion
};
